import { createRouter, createWebHistory, type RouteRecordRaw, type Router } from 'vue-router'

/**
 * Tabla de rutas de la PWA.
 *
 * `meta.layout` decide el shell que envuelve la vista (`auth` o `default`) y
 * `meta.requiresAuth` / `meta.roles` los aplica `registerAuthGuards`.
 *
 * Los componentes se cargan de forma diferida (`() => import(...)`) para que el
 * bundle inicial solo incluya el login.
 */
export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false, layout: 'auth' },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true, roles: ['admin'], layout: 'default' },
  },
  {
    path: '/marketing',
    name: 'marketing',
    component: () => import('@/views/MarketingView.vue'),
    // Por permiso y no por rol: el panel lo ve el marketing y el admin, y el
    // permiso es el que sabe quién lo tiene. Con `roles: ['admin', 'marketing']`
    // sería decidir en el cliente quién entra, que es la lista de nombres otra
    // vez.
    meta: { requiresAuth: true, permission: 'marketing.access', layout: 'default' },
  },
  {
    path: '/quotes',
    name: 'quotes.index',
    component: () => import('@/views/quotes/IndexView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'advisor'], layout: 'default' },
  },
  {
    path: '/quotes/create',
    name: 'quotes.create',
    component: () => import('@/views/quotes/CreateView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'advisor'], layout: 'default' },
  },
  {
    path: '/quotes/:id',
    name: 'quotes.show',
    component: () => import('@/views/quotes/ShowView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'advisor'], layout: 'default' },
  },
  {
    // El cotizador es la vista de edición: abre un borrador nuevo o trabaja sobre
    // uno existente con la misma pantalla, así que no hay una ruta aparte de
    // edición. Editar una cotización ya creada se hace desde su detalle.
    path: '/quotes/:id/edit',
    name: 'quotes.edit',
    // El cotizador, no la ficha. Apuntaba a `ShowView`, que no edita nada: la ruta
    // existía y «abrir en edición» te enseñaba la cotización en lectura, que es la
    // forma más confusa de no tener edición: parece que la hay.
    component: () => import('@/views/quotes/CreateView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'advisor'], layout: 'default' },
  },
  {
    // La tienda se opera con permiso propio. No se restringe por rol: quien
    // tenga `store-sales.manage` entra, y ese permiso ya lo resuelve la guarda.
    path: '/store-sales',
    name: 'store-sales.index',
    component: () => import('@/views/storeSales/IndexView.vue'),
    meta: { requiresAuth: true, permission: 'store-sales.manage', layout: 'default' },
  },
  {
    path: '/store-sales/create',
    name: 'store-sales.create',
    component: () => import('@/views/storeSales/CreateView.vue'),
    meta: { requiresAuth: true, permission: 'store-sales.manage', layout: 'default' },
  },
  {
    // Antes de `/store-sales/:id`: si la ruta genérica fuera primero,
    // `/store-sales/1/edit` se leería como una venta con id `1/edit`.
    path: '/store-sales/:id/edit',
    name: 'store-sales.edit',
    component: () => import('@/views/storeSales/EditView.vue'),
    meta: { requiresAuth: true, permission: 'store-sales.manage', layout: 'default' },
  },
  {
    path: '/store-sales/:id',
    name: 'store-sales.show',
    component: () => import('@/views/storeSales/ShowView.vue'),
    meta: { requiresAuth: true, permission: 'store-sales.manage', layout: 'default' },
  },
  {
    path: '/customers',
    name: 'customers.index',
    component: () => import('@/views/customers/IndexView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'advisor'], layout: 'default' },
  },
  {
    path: '/customers/create',
    name: 'customers.create',
    component: () => import('@/views/customers/CreateView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'advisor'], layout: 'default' },
  },
  {
    // Antes de `/customers/:id`: vue-router evalúa en orden y `/customers/1/edit`
    // no se consumiría como `id: "1"` si la ruta genérica fuera primero.
    path: '/customers/:id/edit',
    name: 'customers.edit',
    component: () => import('@/views/customers/EditView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'advisor'], layout: 'default' },
  },
  {
    path: '/customers/:id',
    name: 'customers.show',
    component: () => import('@/views/customers/ShowView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'advisor'], layout: 'default' },
  },
  // El catálogo tiene dos pantallas, una por tipo, y el tipo va en la ruta.
  //
  // Con una ruta única `/catalog/:id` y el tipo en la query, un enlace a
  // `/catalog/5` es ambiguo: el 5 puede ser un producto o un set. Con el tipo en la
  // ruta, `/catalog/sets/5` lo dice sin mirar nada más.
  ...(['products', 'sets'] as const).flatMap((tipo) => [
    {
      path: `/catalog/${tipo}`,
      name: `catalog.${tipo}.index`,
      component: () => import(`@/views/catalog/${tipo === 'products' ? 'Products' : 'Sets'}IndexView.vue`),
      meta: { requiresAuth: true, roles: ['admin', 'advisor'], layout: 'default' },
    },
    {
      path: `/catalog/${tipo}/create`,
      name: `catalog.${tipo}.create`,
      component: () => import('@/views/catalog/CreateView.vue'),
      meta: { requiresAuth: true, roles: ['admin', 'advisor'], layout: 'default', catalogKind: tipo },
    },
    {
      // Antes de `/catalog/${tipo}/:id`: vue-router evalúa las rutas en orden y
      // `/catalog/sets/edit` se consumiría como `id: "edit"` si fuera después.
      path: `/catalog/${tipo}/:id/edit`,
      name: `catalog.${tipo}.edit`,
      component: () => import('@/views/catalog/EditView.vue'),
      meta: { requiresAuth: true, roles: ['admin', 'advisor'], layout: 'default', catalogKind: tipo },
    },
    {
      path: `/catalog/${tipo}/:id`,
      name: `catalog.${tipo}.show`,
      component: () => import('@/views/catalog/ShowView.vue'),
      meta: { requiresAuth: true, roles: ['admin', 'advisor'], layout: 'default', catalogKind: tipo },
    },
  ]),
  {
    // `/catalog` a secas no significa nada: es la antigua lista única. Se manda a
    // productos, que es donde va quien busca una pieza suelta, y no a un 404.
    path: '/catalog',
    redirect: { name: 'catalog.products.index' },
  },
  {
    path: '/followups',
    name: 'followups.index',
    component: () => import('@/views/followups/IndexView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'advisor'], layout: 'default' },
  },
  {
    // Un seguimiento no se crea suelto: es la fecha de próximo contacto de una
    // cotización. La bandeja enlaza a la cotización y esta ruta existe para los
    // enlaces profundos desde una notificación, de modo que `followups/:id`
    // resuelve exactamente la misma vista que `quotes/:id`.
    path: '/followups/:id',
    name: 'followups.show',
    component: () => import('@/views/quotes/ShowView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'advisor'], layout: 'default' },
  },
  {
    path: '/users',
    name: 'users.index',
    component: () => import('@/views/users/IndexView.vue'),
    meta: { requiresAuth: true, roles: ['admin'], layout: 'default' },
  },
  {
    path: '/users/create',
    name: 'users.create',
    component: () => import('@/views/users/CreateView.vue'),
    meta: { requiresAuth: true, roles: ['admin'], layout: 'default' },
  },
  {
    path: '/users/:id/edit',
    name: 'users.edit',
    component: () => import('@/views/users/EditView.vue'),
    meta: { requiresAuth: true, roles: ['admin'], layout: 'default' },
  },
  {
    path: '/users/:id',
    name: 'users.show',
    component: () => import('@/views/users/ShowView.vue'),
    meta: { requiresAuth: true, roles: ['admin'], layout: 'default' },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/ProfileView.vue'),
    meta: { requiresAuth: true, layout: 'default' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { requiresAuth: false, layout: 'default' },
  },
]

export function createAppRouter(): Router {
  return createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    scrollBehavior(to, from, savedPosition) {
      return savedPosition ?? { top: 0 }
    },
  })
}

/**
 * El tipo del catálogo viaja en el `meta` de la ruta, no en la query.
 *
 * Lo leen las vistas de alta, edición y detalle, que son las mismas para las dos
 * pantallas: lo que cambia es de qué endpoint se lee y cuál se escribe. Con el tipo
 * en la query había que propagarlo por todos los enlaces y perderlo en cuanto una
 * vista redirigía; con el tipo en la ruta, la ruta lo dice siempre.
 */
declare module 'vue-router' {
  interface RouteMeta {
    /** `products` o `sets`, en las rutas del catálogo. */
    catalogKind?: 'products' | 'sets'
    /**
     * Permiso necesario para entrar. Alternativa a `roles` cuando el acceso lo
     * decide un permiso compartido por varios roles.
     */
    permission?: string
  }
}
